/**
 * Create / edit one entry template (V5-35).
 *
 * Plain `useState` rather than react-hook-form + zod. The rules that decide
 * whether a template is valid — name uniqueness among live templates, the
 * category-kind match, the account-ownership check — are all server-side and
 * none of them can be evaluated in the browser, so a client schema would
 * duplicate only the trivial half and still leave the real errors arriving as
 * HTTP. One error channel is easier to reason about than two, and it is the
 * same call the QuickAdd sheet makes.
 *
 * The amount is edited in RUPEES and stored in PAISE. That conversion happens
 * here, at the boundary, exactly once in each direction.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Field, Input, Label, Select, Textarea } from "@/components/ui/Input";
import { CategoryPicker } from "@/components/categories/CategoryPicker";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import { useQuery } from "@tanstack/react-query";

import { PAYMENT_METHOD_OPTIONS, type EntryTemplate, type TemplateType } from "./types";
import { useCreateTemplate, useUpdateTemplate } from "./useTemplates";

interface AccountOption {
  id: string;
  nickname: string | null;
  bankName: string;
}

interface TemplateFormProps {
  open: boolean;
  onClose: () => void;
  /** Present = edit, absent = create. */
  initial?: EntryTemplate | null;
  /** Preselected type for a create opened from a typed surface. */
  defaultType?: TemplateType;
  onSuccess?: (template: EntryTemplate) => void;
}

/** `250000` -> `"2500"`. Empty for a template that asks every time. */
function paiseToRupeeInput(paise: number | null): string {
  return paise === null ? "" : String(paise / 100);
}

export function TemplateForm({
  open,
  onClose,
  initial,
  defaultType = "expense",
  onSuccess,
}: TemplateFormProps) {
  const isEdit = Boolean(initial);

  const [name, setName] = useState("");
  const [type, setType] = useState<TemplateType>(defaultType);
  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [accountId, setAccountId] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("UPI");
  const [description, setDescription] = useState("");

  // Re-seed when the dialog OPENS, or when it is pointed at a different
  // template.
  //
  // The dependency list names `initial?.id`, NOT `initial`. That is load-bearing
  // rather than a lint accommodation: `initial` comes out of the TanStack Query
  // cache and gets a fresh object identity on every refetch, so depending on the
  // object would re-run this effect — and wipe whatever the user had typed —
  // the moment a background refetch landed while the dialog was open. The id is
  // what actually identifies "a different template".
  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setType(initial?.type ?? defaultType);
    setAmount(paiseToRupeeInput(initial?.amountPaise ?? null));
    setCategoryId(initial?.categoryId ?? "");
    setAccountId(initial?.accountId ?? "");
    setPaymentMethod(initial?.paymentMethod ?? "UPI");
    setDescription(initial?.description ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, defaultType]);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts", false],
    queryFn: async () => {
      const res = await apiFetch(apiUrl("accounts", "", { includeArchived: "false" }));
      if (!res.ok) return [] as AccountOption[];
      return (await res.json()) as AccountOption[];
    },
    enabled: open,
  });

  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Give the template a name");
      return;
    }

    const trimmedAmount = amount.trim();
    const parsed = trimmedAmount === "" ? null : Number(trimmedAmount);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      toast.error("Enter a valid amount, or leave it blank to be asked each time");
      return;
    }

    const input = {
      name: name.trim(),
      type,
      amountPaise: parsed === null ? null : Math.round(parsed * 100),
      categoryId: categoryId || null,
      accountId: accountId || null,
      // Income has no payment method to carry.
      paymentMethod: type === "expense" ? paymentMethod || null : null,
      description: description.trim() || null,
    };

    const onDone = (template: EntryTemplate) => {
      toast.success(isEdit ? "Template updated" : "Template saved");
      onSuccess?.(template);
      onClose();
    };
    const onFail = (err: Error) => toast.error(err.message);

    if (isEdit && initial) {
      updateMutation.mutate({ id: initial.id, input }, { onSuccess: onDone, onError: onFail });
    } else {
      createMutation.mutate(input, { onSuccess: onDone, onError: onFail });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={isEdit ? "Edit template" : "New template"}
      description="A saved blueprint. Picking it fills the entry form — it never saves anything on its own."
      width="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="template-form" loading={isPending}>
            {isEdit ? "Save changes" : "Create template"}
          </Button>
        </>
      }
    >
      <form id="template-form" onSubmit={handleSubmit} className="space-y-3">
        <Field>
          <Label htmlFor="tpl-name" required>
            Name
          </Label>
          <Input
            id="tpl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Electricity bill"
            autoFocus
          />
        </Field>

        <Field>
          <Label htmlFor="tpl-type" required>
            Type
          </Label>
          <Select
            id="tpl-type"
            value={type}
            onChange={(e) => {
              const next = e.target.value as TemplateType;
              setType(next);
              // The category belongs to the OTHER kind now, so clear it rather
              // than let the user submit into a guaranteed 400.
              if (next !== type) setCategoryId("");
            }}
          >
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </Select>
        </Field>

        <Field>
          <Label htmlFor="tpl-amount" hint="Leave blank to be asked each time">
            Amount (₹)
          </Label>
          <Input
            id="tpl-amount"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="2500"
          />
        </Field>

        <Field>
          <Label>Category</Label>
          {/* `CategoryPicker` already keeps an ALREADY-SELECTED archived
              category visible while excluding archived ones from the rest of
              the list — the repo-wide archived-category rule. That is exactly
              the behaviour a template needs, so it is reused rather than
              reimplemented: editing a template whose category was archived
              last week must not silently drop the category. */}
          <CategoryPicker
            kind={type === "income" ? "INCOME" : "EXPENSE"}
            value={categoryId || null}
            onChange={(id) => setCategoryId(id ?? "")}
          />
        </Field>

        <Field>
          <Label htmlFor="tpl-account">Account</Label>
          <Select
            id="tpl-account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">No account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.nickname || account.bankName}
              </option>
            ))}
          </Select>
        </Field>

        {type === "expense" && (
          <Field>
            <Label htmlFor="tpl-payment">Payment mode</Label>
            <Select
              id="tpl-payment"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field>
          <Label htmlFor="tpl-description">Description</Label>
          <Textarea
            id="tpl-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="MSEB monthly bill"
          />
        </Field>
      </form>
    </Dialog>
  );
}
